/**
 * School Logos Configuration & Helper Utilities
 * Maps school shortnames, school IDs, and OpenOlat/LMS hostnames to logo assets.
 */

export interface SchoolLogoItem {
  logo: string;
  name: string;
  shortname: string;
}

export const SCHOOL_LOGO_MAP: Record<string, SchoolLogoItem> = {
  HSO: { logo: '/hso_logo.png', name: 'HSO Wirtschaftsschule Schweiz', shortname: 'HSO' },
  BVS: { logo: '/bvs_logo.png', name: 'BVS St. Gallen', shortname: 'BVS' },
  SMA: { logo: '/sma_logo.png', name: 'Swiss Marketing Academy', shortname: 'SMA' },
  IST: { logo: '/ist_logo.png', name: 'IST AG', shortname: 'IST' },
  PRA: { logo: '/pra_logo.png', name: 'Prävensana', shortname: 'PRA' },
  APA: { logo: '/apa_logo.png', name: 'APAMED', shortname: 'APA' },
  MBS: { logo: '/mbs_logo.png', name: 'Marketing & Business School Zürich', shortname: 'MBS' },
  NOV: { logo: '/nov_logo.png', name: 'Nova Academy', shortname: 'NOV' },
  OSD: { logo: '/osd_logo.png', name: 'Oekreal School of Business', shortname: 'OSD' },
  BOF: { logo: '/logo_vantage.svg', name: 'Bodyfeet AG', shortname: 'BOF' },
  BIO: { logo: '/logo_vantage.svg', name: 'Bio-Medica Fachschule', shortname: 'BIO' },
  SOA: { logo: '/soa_logo.svg', name: 'Swiss Online Academy', shortname: 'SOA' },
  VAN: { logo: '/logo_vantage.svg', name: 'Vantage Education', shortname: 'VAN' },
};

/**
 * Domain & Hostname-to-School mapping for OpenOlat, LMS, and external portal entry points.
 * Handles domains and hostnames like:
 * - openolat.hso.ch, hsotest.openolat.com -> HSO
 * - lms.vantage.ch, lms-test.vantage.ch -> VAN
 * - openolat.swissmarketingacademy.ch -> SMA
 * - openolat.ist-edu.ch -> IST
 * - openolat.praevensana.ch -> PRA
 * - openolat.apamed.ch -> APA
 * - openolat.mbsz.ch -> MBS
 * - openolat.novaacademy.ch -> NOV
 * - openolat.bodyfeet.ch, bodyfeet.ch -> BOF
 * - openolat.bvs.ch -> BVS
 */
export const DOMAIN_TO_SCHOOL_MAP: Record<string, string> = {
  // Specific full hostnames (checked first)
  'hsotest.openolat.com': 'HSO',
  'openolat.bodyfeet.ch': 'BOF',
  'bodyfeet.ch': 'BOF',
  'openolat.hso.ch': 'HSO',
  'hso.ch': 'HSO',
  'bvs.ch': 'BVS',
  'openolat.bvs.ch': 'BVS',
  'swissmarketingacademy.ch': 'SMA',
  'sma.ch': 'SMA',
  'ist-edu.ch': 'IST',
  'ist.ch': 'IST',
  'praevensana.ch': 'PRA',
  'apamed.ch': 'APA',
  'mbsz.ch': 'MBS',
  'novaacademy.ch': 'NOV',
  'oekreal.ch': 'OSD',
  'swissonlineacademy.ch': 'SOA',
};

/**
 * Resolves a school code/shortname from a hostname, redirect_uri, or referrer URL.
 * e.g. "https://openolat.hso.ch/dmz/" -> "HSO"
 *      "https://hsotest.openolat.com/dmz/" -> "HSO"
 *      "https://openolat.bodyfeet.ch/" -> "BOF"
 *      "https://openolat.swissmarketingacademy.ch/auth/CourseSite/1" -> "SMA"
 *      "https://openolat.ist-edu.ch/auth/CourseSite/1" -> "IST"
 *      "https://openolat.praevensana.ch/auth/CourseSite/1" -> "PRA"
 *      "https://openolat.apamed.ch/auth/CourseSite/1" -> "APA"
 *      "https://openolat.mbsz.ch/" -> "MBS"
 *      "https://openolat.novaacademy.ch/" -> "NOV"
 *      "https://lms.vantage.ch/" -> "VAN"
 *      "https://lms-test.vantage.ch/maintenance/" -> "VAN"
 */
export function resolveSchoolFromUrl(rawUrlOrHost: string | null | undefined): string | null {
  if (!rawUrlOrHost) return null;

  try {
    let hostname = rawUrlOrHost.toLowerCase().trim();
    if (hostname.includes('://')) {
      const parsed = new URL(hostname);
      hostname = parsed.hostname;
    } else {
      // Remove paths/query if present
      hostname = hostname.split('/')[0].split('?')[0];
    }

    // Bypass shared Vantage infrastructure domains (shared across all schools)
    // Default portals and shared LMS instances should display the full scrolling banner
    if (
      hostname === 'vantage.ch' ||
      hostname.endsWith('.vantage.ch') ||
      hostname === 'portal.vantage.ch' ||
      hostname === 'test-portal.vantage.ch' ||
      hostname === 'lms.vantage.ch' ||
      hostname === 'lms-test.vantage.ch'
    ) {
      // Check if a specific school prefix exists on vantage domain (e.g. hso.vantage.ch)
      if (hostname.includes('hso')) return 'HSO';
      if (hostname.includes('bodyfeet')) return 'BOF';
      if (hostname.includes('sma') || hostname.includes('marketingacademy')) return 'SMA';
      if (hostname.includes('ist')) return 'IST';
      if (hostname.includes('pra')) return 'PRA';
      if (hostname.includes('apa')) return 'APA';
      if (hostname.includes('mbs')) return 'MBS';
      if (hostname.includes('nova')) return 'NOV';
      if (hostname.includes('bvs')) return 'BVS';
      if (hostname.includes('osd') || hostname.includes('oekreal')) return 'OSD';
      if (hostname.includes('soa') || hostname.includes('swissonline')) return 'SOA';
      // Otherwise it's shared Vantage parent infrastructure -> return null to keep full banner
      return null;
    }

    // 1. Check exact match or subdomain match against domain map
    for (const [domain, school] of Object.entries(DOMAIN_TO_SCHOOL_MAP)) {
      if (hostname === domain || hostname.endsWith(`.${domain}`)) {
        return school;
      }
    }

    // 2. Direct keyword / school shortname matching in hostname
    if (hostname.includes('hsotest') || hostname.includes('hso')) return 'HSO';
    if (hostname.includes('bodyfeet')) return 'BOF';
    if (hostname.includes('marketingacademy') || hostname.includes('sma')) return 'SMA';
    if (hostname.includes('ist-edu') || hostname.includes('ist.')) return 'IST';
    if (hostname.includes('praevensana') || hostname.includes('pra')) return 'PRA';
    if (hostname.includes('apamed') || hostname.includes('apa')) return 'APA';
    if (hostname.includes('mbsz') || hostname.includes('mbs')) return 'MBS';
    if (hostname.includes('novaacademy') || hostname.includes('nova')) return 'NOV';
    if (hostname.includes('bvs')) return 'BVS';
    if (hostname.includes('oekreal') || hostname.includes('osd')) return 'OSD';
    if (hostname.includes('swissonline') || hostname.includes('soa')) return 'SOA';

    // 3. Fallback: check if hostname contains known school identifier directly (excluding VAN / parent)
    for (const school of Object.keys(SCHOOL_LOGO_MAP)) {
      if (school === 'VAN') continue;
      const sLower = school.toLowerCase();
      if (hostname.includes(sLower)) {
        return school;
      }
    }
  } catch {
    // URL parsing fallback for malformed or raw strings
    const lower = rawUrlOrHost.toLowerCase();
    for (const [domain, school] of Object.entries(DOMAIN_TO_SCHOOL_MAP)) {
      if (lower.includes(domain)) {
        return school;
      }
    }
  }

    return null;
}

/**
 * Returns human-readable school name for a shortname or falls back to the shortname itself
 */
export function getSchoolDisplayName(schoolCode: string | null | undefined): string {
  if (!schoolCode) return '';
  const item = SCHOOL_LOGO_MAP[schoolCode.toUpperCase().trim()];
  return item ? item.name : schoolCode;
}
